import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@nimit9/signet-ui/components/drawer"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useEffect } from "react"
import { cn } from "@/lib/cn"
import { useClosePanel, usePanel } from "@/store/selectors"

interface PanelContentProps {
  type: string
  data: Record<string, unknown>
}

interface PanelRootProps {
  renderContent: (props: PanelContentProps) => React.ReactNode
}

/**
 * Detail-view container for panel data (transactions, goals, …).
 *
 * Phone (< md): a swipeable bottom Drawer (S4) — the ShadCN/@base-ui primitive
 * in @nimit9/signet-ui, not a hand-rolled sheet.
 * Desktop (>= md): the existing fixed right-hand side panel, unchanged.
 */
export function PanelRoot({ renderContent }: PanelRootProps) {
  const panel = usePanel()
  const closePanel = useClosePanel()
  const isDesktop = useMediaQuery("(min-width: 768px)")

  // Close on Escape — desktop panel only (the Drawer handles its own Escape/
  // swipe/backdrop dismissal internally via @base-ui).
  useEffect(() => {
    if (isDesktop && panel) {
      function onKey(e: KeyboardEvent) {
        if (e.key === "Escape") closePanel()
      }
      document.addEventListener("keydown", onKey)
      return () => document.removeEventListener("keydown", onKey)
    }
  }, [panel, closePanel, isDesktop])

  if (!isDesktop) {
    return (
      <Drawer
        open={!!panel}
        onOpenChange={(open) => {
          if (!open) closePanel()
        }}
        showSwipeHandle
      >
        <DrawerContent>
          <DrawerHeader className="flex-row items-center justify-between pb-3">
            <DrawerTitle className="capitalize">{panel?.type.replace(/_/g, " ") ?? ""}</DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {panel && renderContent({ type: panel.type, data: panel.data })}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 transition-opacity duration-200",
          panel ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={closePanel}
      />

      {/* Panel */}
      <div
        // Always mounted for the slide transition; only a (named) dialog while open,
        // and inert while closed so it isn't an empty, unnamed dialog (axe aria-dialog-name).
        {...(panel
          ? { role: "dialog", "aria-modal": true, "aria-labelledby": "panel-title" }
          : { inert: true })}
        className={cn(
          "fixed right-0 top-0 z-50 flex h-full w-[360px] flex-col border-l border-border-default bg-bg-surface transition-transform duration-200 ease-out",
          panel ? "translate-x-0" : "translate-x-full",
        )}
      >
        {panel && (
          <>
            {/* Header */}
            <div className="flex h-[52px] shrink-0 items-center justify-between border-b border-border-default px-5">
              <h2 id="panel-title" className="text-[14px] font-medium text-text-primary capitalize">
                {panel.type.replace(/_/g, " ")}
              </h2>
              <button
                type="button"
                onClick={closePanel}
                aria-label="Close panel"
                className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-bg-elevated hover:text-text-secondary"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {renderContent({ type: panel.type, data: panel.data })}
            </div>
          </>
        )}
      </div>
    </>
  )
}
